import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { useAnalytics } from "../hooks/useAnalytics";
import "./Words.css";

const Words = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { words, updateWordCategory, loading } = useData();
  const { trackAudio } = useAnalytics();
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingWord, setEditingWord] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleEditCategory = (category) => {
    setEditingCategory({ ...category });
  };

  const handleSaveCategory = async () => {
    try {
      await updateWordCategory(editingCategory.id, editingCategory);
      setEditingCategory(null);
    } catch (error) {
      alert("Failed to save changes. Please try again.");
    }
  };

  const handleEditWord = (categoryId, wordIndex, word) => {
    setEditingWord({ categoryId, wordIndex, ...word });
    // Reset recording state when starting to edit
    setRecordedAudio(null);
    setIsRecording(false);
    setMediaRecorder(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setRecordedAudio(url);
        // Stop all tracks to release the microphone
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Failed to access microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleSaveWord = async () => {
    try {
      const category = words.find((c) => c.id === editingWord.categoryId);
      const updatedItems = [...category.items];
      updatedItems[editingWord.wordIndex] = {
        japanese: editingWord.japanese,
        romaji: editingWord.romaji,
        english: editingWord.english,
        audio: editingWord.audio,
      };

      await updateWordCategory(category.id, {
        ...category,
        items: updatedItems,
      });

      setEditingWord(null);
    } catch (error) {
      alert("Failed to save word. Please try again.");
    }
  };

  const playAudio = (audioUrl, wordInfo) => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch((e) => console.log("Error playing audio:", e));
      trackAudio("word", wordInfo);
    }
  };

  if (loading) {
    return (
      <div className="words">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="words">
      <div className="header">
        <button className="back-button" onClick={handleBackToHome}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>
      </div>

      <div className="main-content">
        <h1 className="title">Japanese Words</h1>

        <div className="word-categories">
          {words.map((category) => (
            <div key={category.id} className="word-card">
              <div className="word-card-header">
                <h2>
                  {editingCategory && editingCategory.id === category.id ? (
                    <input
                      type="text"
                      value={editingCategory.category}
                      onChange={(e) =>
                        setEditingCategory({
                          ...editingCategory,
                          category: e.target.value,
                        })
                      }
                      className="edit-input"
                    />
                  ) : (
                    category.category
                  )}
                </h2>
                {isAdmin && (
                  <div className="admin-controls">
                    {editingCategory && editingCategory.id === category.id ? (
                      <>
                        <button
                          onClick={handleSaveCategory}
                          className="save-btn"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingCategory(null)}
                          className="cancel-btn"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEditCategory(category)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="word-list">
                {category.items?.map((word, index) => (
                  <div key={index} className="word-item">
                    {editingWord &&
                    editingWord.categoryId === category.id &&
                    editingWord.wordIndex === index ? (
                      <div className="word-edit">
                        <input
                          type="text"
                          value={editingWord.japanese}
                          onChange={(e) =>
                            setEditingWord({
                              ...editingWord,
                              japanese: e.target.value,
                            })
                          }
                          className="japanese-input"
                          placeholder="Japanese"
                        />
                        <input
                          type="text"
                          value={editingWord.romaji}
                          onChange={(e) =>
                            setEditingWord({
                              ...editingWord,
                              romaji: e.target.value,
                            })
                          }
                          className="romaji-input"
                          placeholder="Romaji"
                        />
                        <input
                          type="text"
                          value={editingWord.english}
                          onChange={(e) =>
                            setEditingWord({
                              ...editingWord,
                              english: e.target.value,
                            })
                          }
                          className="english-input"
                          placeholder="English"
                        />
                        <input
                          type="url"
                          value={editingWord.audio}
                          onChange={(e) =>
                            setEditingWord({
                              ...editingWord,
                              audio: e.target.value,
                            })
                          }
                          className="audio-input"
                          placeholder="MP3 URL"
                        />
                        <div className="audio-recording-controls">
                          {!isRecording ? (
                            <button
                              onClick={startRecording}
                              className="record-btn"
                              title="Start recording"
                            >
                              🎤 Record
                            </button>
                          ) : (
                            <button
                              onClick={stopRecording}
                              className="stop-btn"
                              title="Stop recording"
                            >
                              ⏹️ Stop
                            </button>
                          )}
                          {recordedAudio && (
                            <>
                              <audio
                                src={recordedAudio}
                                controls
                                className="audio-preview"
                              />
                              <button
                                onClick={() => {
                                  setEditingWord({
                                    ...editingWord,
                                    audio: recordedAudio,
                                  });
                                }}
                                className="use-recording-btn"
                                title="Use this recording"
                              >
                                ✓ Use Recording
                              </button>
                            </>
                          )}
                        </div>
                        <div className="word-controls">
                          <button onClick={handleSaveWord} className="save-btn">
                            Save
                          </button>
                          <button
                            onClick={() => setEditingWord(null)}
                            className="cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="word-display">
                        <span className="japanese">{word.japanese}</span>
                        <span className="romaji">{word.romaji}</span>
                        <span className="english">{word.english}</span>
                        <div className="word-controls">
                          {word.audio && (
                            <button
                              onClick={() =>
                                playAudio(
                                  word.audio,
                                  `${category.id}:${word.japanese}`
                                )
                              }
                              className="audio-btn"
                              title="Play pronunciation"
                            >
                              🔊
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() =>
                                handleEditWord(category.id, index, word)
                              }
                              className="edit-word-btn"
                              title="Edit word"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Word Modal */}
      {editingWord && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Word</h3>
            <div className="edit-form">
              <label>
                Japanese:
                <input
                  type="text"
                  value={editingWord.japanese}
                  onChange={(e) =>
                    setEditingWord({ ...editingWord, japanese: e.target.value })
                  }
                />
              </label>
              <label>
                Romaji:
                <input
                  type="text"
                  value={editingWord.romaji}
                  onChange={(e) =>
                    setEditingWord({ ...editingWord, romaji: e.target.value })
                  }
                />
              </label>
              <label>
                English:
                <input
                  type="text"
                  value={editingWord.english}
                  onChange={(e) =>
                    setEditingWord({ ...editingWord, english: e.target.value })
                  }
                />
              </label>
              <label>
                Audio URL (MP3):
                <input
                  type="url"
                  value={editingWord.audio}
                  onChange={(e) =>
                    setEditingWord({ ...editingWord, audio: e.target.value })
                  }
                  placeholder="https://example.com/audio.mp3"
                />
              </label>
              <div className="audio-recording-section">
                <label>Record Audio:</label>
                <div className="audio-recording-controls">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      className="record-btn"
                      title="Start recording"
                    >
                      🎤 Record
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="stop-btn"
                      title="Stop recording"
                    >
                      ⏹️ Stop
                    </button>
                  )}
                  {recordedAudio && (
                    <>
                      <audio
                        src={recordedAudio}
                        controls
                        className="audio-preview"
                      />
                      <button
                        onClick={() => {
                          setEditingWord({
                            ...editingWord,
                            audio: recordedAudio,
                          });
                        }}
                        className="use-recording-btn"
                        title="Use this recording"
                      >
                        ✓ Use Recording
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-controls">
                <button onClick={handleSaveWord} className="save-btn">
                  Save
                </button>
                <button
                  onClick={() => setEditingWord(null)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Words;
